import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  Dividenduitkering,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {debounceTime, distinctUntilChanged, filter, Observable, shareReplay, Subscription, take} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-dividends',
  templateUrl: './dividends.component.html',
  styleUrls: ['./dividends.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class DividendsComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  private bedragSubscriptions = new Subscription();

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public dividendsFg = new FormGroup({
    dividenden: new FormArray<
      FormGroup<{
        datumUitkering: FormControl<Date | null>;
        datumAangifteDividendbelasting: FormControl<Date | null>;
        ingehoudenDividendbelasting: FormControl<number | null>;
        bedragDividenduitkering: FormControl<number | null>;
      }>
    >([]),
  });

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.updateForm(data.aangifte!);
    })
    this.watchBedragChanges();
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  get dividenden(): FormArray<FormGroup<{
    datumUitkering: FormControl<Date | null>;
    datumAangifteDividendbelasting: FormControl<Date | null>;
    ingehoudenDividendbelasting: FormControl<number | null>;
    bedragDividenduitkering: FormControl<number | null>;
  }>> {
    return this.dividendsFg.controls.dividenden;
  }

  addRij(): void {
    this.dividenden.push(this.createFormGroup(), { emitEvent: false });
    this.watchBedragChanges();
  }

  verwijderRij(index: number): void {
    this.dividenden.removeAt(index, { emitEvent: false });
    this.watchBedragChanges();
  }

  createFormGroup(item?: Dividenduitkering): FormGroup {
    return new FormGroup({
      bedragDividenduitkering: new FormControl<number | null>(item?.bedragDividenduitkering ?? null),
      datumAangifteDividendbelasting: new FormControl<Date | null>(item?.datumAangifteDividendbelasting ?? null),
      datumUitkering: new FormControl<Date | null>(item?.datumUitkering ?? null),
      ingehoudenDividendbelasting: new FormControl<number | null>(item?.ingehoudenDividendbelasting ?? null),
    });
  }

  private watchBedragChanges(): void {
    this.bedragSubscriptions.unsubscribe();
    this.bedragSubscriptions = new Subscription();

    this.dividenden.controls.forEach(group => {
      this.bedragSubscriptions.add(
        group.controls.bedragDividenduitkering.valueChanges.pipe(
          debounceTime(500),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
          this.saveForm();
          this.dataService.calculateLatestCorporateTaxReturn().subscribe();
        })
      );
    });
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = vpbAangifte.specificatieNietAftrekbareBedragen?.dividenduitkeringen || [];
    const formArray = this.dividendsFg.get('dividenden') as FormArray;

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i])
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.dividendsFg.updateValueAndValidity({ emitEvent: false });

    const dividenduitkering: Dividenduitkering[] =
      this.dividendsFg.getRawValue().dividenden
        .filter((item): item is {
          datumUitkering: Date;
          datumAangifteDividendbelasting: Date | null;
          ingehoudenDividendbelasting: number;
          bedragDividenduitkering: number;
        } =>
          item.datumUitkering != null &&
          item.ingehoudenDividendbelasting != null &&
          item.bedragDividenduitkering != null
        )
        .map(item => ({
          bedragDividenduitkering: item.bedragDividenduitkering,
          ingehoudenDividendbelasting: item.ingehoudenDividendbelasting,
          datumUitkering: item.datumUitkering,
          datumAangifteDividendbelasting: item.datumAangifteDividendbelasting ?? undefined,
        }));

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieNietAftrekbareBedragen: {
            ...currentEntity.aangifte.specificatieNietAftrekbareBedragen,
            dividenduitkeringen: dividenduitkering,
          },
        },
      } as CorporateTaxEntity;
    });
  }
}
